// 未ログインまたは面談済お子さまがいない場合の空き状況カレンダーページ
import { useCallback, useEffect, useState } from 'react';
import { useForm, useWatch, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { 
  yesNo, 
  useReasonCode as useReasonCodeValue, 
  purposeOfUse as purposeOfUseValue,
  lotteryAcceptTypeOption,
  useReasonDisplayTypes
} from '../common/constant/classification';
import { getUsageGuidStr } from '../common/reservationSelection';
import { getUseReason } from '../common/useReason'
import { Child, LocationState as LoggedInLocationState, getDiagonosisDecodeMstValue } from '../facilityReservationSelection/facilityReservationSelectionService';
import { executeGetChildren } from '../../dataAccess/webApi/dao/childrenDao'
import { executeGetFacilityReservationsWeekPublic } from '../../dataAccess/webApi/dao/facilityReservationsWeekPublicDao';
import { GetFacilityDto } from '../../dataAccess/webApi/dto/facilitiesDto';
import { GetFacilityReservationsWeekPublicDto } from '../../dataAccess/webApi/dto/facilityReservationsWeekPublicDto';
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto';
import { 
  ElapsedMillisecond, 
  isValidDate, 
  toApiYmd, 
  getBirthday, 
  getFromToBirthdayFromClassAge
} from '../../utils/dateUtil';
import { NullPropsToUndefinedType } from '../../utils/typeUtil'
import { useErrorHandle } from '../common/error/errorHandler'
import { getFacility } from '../common/facility'
import { notifyMessage, showLoading } from '../common/store/slices/application';
import { selectSystemControl } from '../common/store/slices/systemControl';
import { selectIsLoggedIn } from '../common/store/slices/authority';
import { facilityReservationSelectionCreateUrl } from '../common/constant/appUrl';
import { UseReasonOptions } from '../../views/components/facilityReservationForm/useReasonRadioGroup';
import { translate } from '../../i18n';
import { SelectChangeEvent } from '@mui/material';

interface UrlParams {
  facilityId: string
  activeChildId?: string
};

type DetailDescription = {
  value: string | null
};
interface Inputs {
  purposeOfUse?: string | null
  useReasonDetailIrregular: string
  useReasonDetailEmg: string
  useReasonDetailRefresh: string
  useReasonDetailDescriptionsIrregular: DetailDescription[]
  useReasonDetailDescriptionsEmg: DetailDescription[]
  useReasonDetailDescriptionsRefresh: DetailDescription[]
};

type AcceptAge = {
  age: number | null
  month: number | null
  day: number | null
  classFlag: boolean
}

export interface LocationState {
  activeDate: ElapsedMillisecond | null // 空き状況確認基準日
  // 選択可能生年月日判定に使用する施設受け入れ可能年齢情報
  acceptAgeFrom: AcceptAge
  acceptAgeTo: AcceptAge
};

type CommonFlag = typeof yesNo[keyof typeof yesNo]
export interface PageState  {
  childBirthday: Date // お子様生年月日の文字列(YYYYMMDD)
  activeDate: Date // 空き状況確認基準日
  facilityId: string
  facility: NullPropsToUndefinedType<GetFacilityDto> // 施設情報
  purposeOfUse?: string | null
  commonFlag: CommonFlag
  useReasonOptions: UseReasonOptions
  usageGuideStr: string
  useReason: NullPropsToUndefinedType<GetUseReasonDto> // 利用目的マスタ情報
  availabilities: GetFacilityReservationsWeekPublicDto[]
  // 選択可能生年月日判定に使用する施設受け入れ可能年齢情報
  acceptAgeFrom: AcceptAge
  acceptAgeTo: AcceptAge
  // 選択可能下限・上限生年月日（７日分表示するので一番未来の日付において選択可能な生年月日を選択可能とする）
  minBirthday: Date
  maxBirthday: Date
  diagonosis: {
    first: string | null,
    second: string | null
  }
  diagonosisDecodeMst: { value: string; label: string }[]
  isolationFlag: Boolean | null,
  acceptLottery: boolean,
  note: string | null,
  lotteryNote: string | null
};

export const useAction = () => {
  const errorHandle = useErrorHandle();
  const dispatch = useDispatch();
  const sysCtrl = useSelector(selectSystemControl);
  const history = useHistory<LocationState | undefined>();
  const nextHistory = useHistory<LoggedInLocationState |undefined>()
  const urlParams = useParams<UrlParams>();

  const isLoggedIn: boolean = useSelector(selectIsLoggedIn);

  const locationState: LocationState | undefined = history.location.state;
  const currentLocation = history.location;

  const notifyDateFetchErrorMessage = useCallback(() => 
    dispatch(notifyMessage(translate('facilityDetailAvailability.error.noReservationReceptionSetting'))
  ), [dispatch]);

  const [state, setState] = useState<PageState>();
  
  // 未ログイン用選択生年月日上限＆下限
  // ７日分表示するので一番未来の日付において選択可能な生年月日を選択可能とする
  
  // フォーム関連
  const formMethods = useForm<Inputs>();
  const purposeOfUse = useWatch({ name: 'purposeOfUse', control: formMethods.control })
  const useReasonDetailIrregular = useWatch({ name: 'useReasonDetailIrregular', control: formMethods.control })
  const useReasonDetailEmg = useWatch({ name: 'useReasonDetailEmg', control: formMethods.control })
  const useReasonDetailRefresh = useWatch({ name: 'useReasonDetailRefresh', control: formMethods.control })
  const { append: appendUseReasonDetailDescriptionsIrregular } = useFieldArray({
    name: 'useReasonDetailDescriptionsIrregular',
    control: formMethods.control,
  })
  const { append: appendUseReasonDetailDescriptionsEmg } = useFieldArray({
    name: 'useReasonDetailDescriptionsEmg',
    control: formMethods.control,
  })
  const { append: appendUseReasonDetailDescriptionsRefresh } = useFieldArray({
    name: 'useReasonDetailDescriptionsRefresh',
    control: formMethods.control,
  })

  const getNotes = useCallback((
    facility: NullPropsToUndefinedType<GetFacilityDto>, 
    acceptLottery: boolean, 
    purposeOfUse: string | null | undefined,
    commonFlag: typeof yesNo [keyof typeof yesNo]
  ) => {
    const returnObject: {
      note: string | null,
      lotteryNote: string | null
    } = {
      note: null,
      lotteryNote: null
    }

    if ( commonFlag === yesNo.no && purposeOfUse === undefined ) return returnObject;

    if ( !purposeOfUse ) {
      // 共通設定
      returnObject.note = facility.detailSetting[0].note;
      if ( acceptLottery && facility.lotteryNotes ) {
        returnObject.lotteryNote = facility.lotteryNotes[0].note;
      }
    } else {
      // 利用目的が選択されている場合、または利用・抽選の何れかのみが利用目的別の設定の場合
      returnObject.note = facility.detailSetting.find((setting) => setting.useReasonCode === purposeOfUse)?.note ?? facility.detailSetting[0].note;
      if ( acceptLottery && facility.lotteryNotes ) {
        if ( facility.lotteryAcceptType === lotteryAcceptTypeOption.common ) {
          returnObject.lotteryNote = facility.lotteryNotes[0].note;
        } else {
          returnObject.lotteryNote = facility.lotteryNotes.find((setting) => setting.useReasonCode === purposeOfUse)?.note?? null;
        }
      }
    }

    return returnObject;
  }, [])
  
  // 初期化
  useEffect(() => {
    if (!locationState) {
      notifyDateFetchErrorMessage();
      return;
    }

    dispatch(
      showLoading(
        errorHandle(async () => {
          const datecriteria = locationState?.activeDate ? new Date(locationState.activeDate) : sysCtrl.systemDatetime? new Date(sysCtrl.systemDatetime) : new Date();
          
          const [useReason, facility, children, diagonosisDecodeMst] = await Promise.all([
            getUseReasonMaster(),
            getFacility(urlParams.facilityId, datecriteria),
            isLoggedIn ? await getChildrenData(urlParams.facilityId) : null,
            getDiagonosisDecodeMstValue()
          ]);
          if (children && children.length > 0 && facility.reservationAcceptFlag === yesNo.yes) {
            // 施設にて許可がされていれば予約画面へ遷移
            const historyState: LoggedInLocationState = {
              isKeep: false,
              activeDate: datecriteria.getTime(),
              children
            };
            const selectedChildId = urlParams.activeChildId?? children[0].value;
            nextHistory.replace({
              pathname: facilityReservationSelectionCreateUrl.url(
                urlParams.facilityId,
                selectedChildId
              ),
              state: historyState,
            });
          }
          
          // 予約受付設定を設定しているか
          if (facility.detailSetting.length === 0 || facility.detailSetting[0].useReasonCode === null) {
            notifyDateFetchErrorMessage();
            return;
          }

          const childBirthday = getAcceptBirthdayTo(locationState.acceptAgeFrom, datecriteria);
          let purposeOfUse: string | null | undefined = undefined;
          const useReasonDetailDescriptionsIrregular: DetailDescription[] = Array(useReason.detailsIrregular.length).fill(null).map(() => ({ value: null }));
          const useReasonDetailDescriptionsEmg: DetailDescription[] = Array(useReason.detailsEmg.length).fill(null).map(() => ({ value: null }));
          const useReasonDetailDescriptionsRefresh: DetailDescription[] = Array(useReason.detailsRefresh.length).fill(null).map(() => ({ value: null }));

          // 抽選予約を受け付けているか
          const acceptLottery =  (
            sysCtrl.lotteryDisplayFlag === yesNo.yes 
            && sysCtrl.lotteryFlag === yesNo.yes 
            && facility.lotteryFlag === yesNo.yes 
          )

          // 各利用目的の利用可否
          const useReasonOptions: UseReasonOptions = {
            irregular: (
              facility.irregularDisplayType === useReasonDisplayTypes.usedInBoth && facility.useReasonUsageFlagIrregular === yesNo.yes
            ),
            emergency: (
              facility.emergencyDisplayType === useReasonDisplayTypes.usedInBoth && facility.useReasonUsageFlagEmg === yesNo.yes
            ),
            refresh: (
              facility.refreshDisplayType === useReasonDisplayTypes.usedInBoth && facility.useReasonUsageFlagRefresh === yesNo.yes
            )
          };

          // 共通OR利用目的別設定かの判断
          let commonFlag: CommonFlag = yesNo.no;
          if (
            facility.detailSetting.length === 1 
            && facility.detailSetting[0].useReasonCode === useReasonCodeValue.common 
            && ( !acceptLottery || facility.lotteryAcceptType === lotteryAcceptTypeOption.common)
          ) {  
            commonFlag = yesNo.yes;
            purposeOfUse = null;
          }

          // カレンダー説明文
          const usageGuideStr = getUsageGuidStr(
            facility.immediatelyReservationFlag,
            facility.cancelWaitAcceptFlag,
            facility.postponeCancelWaitAcceptFlag,
            acceptLottery,
            true
          );

          appendUseReasonDetailDescriptionsIrregular(useReasonDetailDescriptionsIrregular);
          appendUseReasonDetailDescriptionsEmg(useReasonDetailDescriptionsEmg);
          appendUseReasonDetailDescriptionsRefresh(useReasonDetailDescriptionsRefresh);
          const diagonosis = { first: null, second: null }

          const availabilities = purposeOfUse === undefined ? [] : await getAvaiabilitiesForWeek(
            urlParams.facilityId,
            childBirthday,
            datecriteria,
            purposeOfUse,
            diagonosis
          )

          const { note, lotteryNote } = getNotes(facility, acceptLottery, purposeOfUse, commonFlag);

          formMethods.reset({
            purposeOfUse,
            // 必要ない？
            useReasonDetailIrregular,
            useReasonDetailEmg,
            useReasonDetailRefresh,
            useReasonDetailDescriptionsIrregular,
            useReasonDetailDescriptionsEmg,
            useReasonDetailDescriptionsRefresh,
          })
          setState({
            childBirthday,
            activeDate: datecriteria,
            facilityId: urlParams.facilityId,
            facility,
            purposeOfUse,
            commonFlag,
            useReasonOptions,
            usageGuideStr,
            useReason,
            availabilities,
            acceptAgeFrom: locationState.acceptAgeFrom,
            acceptAgeTo: locationState.acceptAgeTo,
            minBirthday: getAcceptBirthdayFrom(locationState.acceptAgeTo, datecriteria),
            maxBirthday: getAcceptBirthdayTo(locationState.acceptAgeFrom, datecriteria),
            diagonosisDecodeMst,
            isolationFlag: facility.isolationRoomUsageFlag === yesNo.yes,
            diagonosis,
            acceptLottery,
            note,
            lotteryNote
          })
        })
      )      
    )

  }, []);

  // 条件が変わったときに空き状況確認する
  useEffect(() => {
    if (!state) return
      
    dispatch(
      showLoading({
        process: errorHandle(async () => {
          if (state.purposeOfUse === undefined) return
          if (state.isolationFlag && !Object.values(state.diagonosis).some(v => v != null) || state.isolationFlag === null) return
            
          const availabilities = await getAvaiabilitiesForWeek(
            state.facilityId,
            state.childBirthday,
            state.activeDate,
            state.purposeOfUse,
            state.diagonosis
          )

          if (availabilities) {
            setState((prev) => ({
              ... prev as PageState,
              availabilities
            }))
          } else {
            // 異常処理
            notifyDateFetchErrorMessage()
          }
        }),
        isHiddenMain: false,
      })
    );
  }, [
      state?.childBirthday,
      state?.activeDate,
      state?.purposeOfUse,
      state?.diagonosis
    ]
  );

  // 日付変更
  const changeDate = useCallback((date: Date | null): void => {
    if (!state || !date) return;

    if (!isValidDate(date)) {
      notifyDateFetchErrorMessage();
      return;
    }

    const minBirthday = getAcceptBirthdayFrom(state.acceptAgeTo, date);
    const maxBirthday = getAcceptBirthdayTo(state.acceptAgeFrom, date);

    const isInvalid = isChildBirthdayInvalid(state.childBirthday, minBirthday, maxBirthday);

    if (isInvalid) {
      notifyDateFetchErrorMessage();
    }
    
    // 変更後の日付から再度隔離フラグを取得、変更
    const resetIsolationFlag = async () => {
      const facility = await getFacility(urlParams.facilityId, state.activeDate)
  
      setState((prev) => ({
        ...prev as PageState,
        isolationFlag: facility.isolationRoomUsageFlag === yesNo.yes,
        activeDate: date,
        minBirthday,
        maxBirthday,
        facility
      }));
    }
    resetIsolationFlag()

    setState((prev) => ({
      ...prev as PageState,
      activeDate: date,
      minBirthday,
      maxBirthday
    }))
  }, [state]);

  // お子様生年月日変更
  const changeChildBirthday = useCallback((date: Date | null): void => {
    if (!state || !date) return;

    const isInvalid = isChildBirthdayInvalid(date, state.minBirthday, state.maxBirthday);
    if (isInvalid) {
      notifyDateFetchErrorMessage();
    }

    setState((prev) => ({
      ...prev as PageState,
      childBirthday: date,
    }))
  }, [state])
  
  // 利用目的変更
  const onChangeUseReason = useCallback((newPurpose: string): void => {
    if (
      state?.commonFlag === undefined ||
      state?.facility === undefined ||
      state?.acceptLottery === undefined
    ) return
    formMethods.reset({
      ...formMethods.getValues(),
      purposeOfUse: newPurpose,
    })

    setState((prev) => ({
      ...prev as PageState,
      purposeOfUse: newPurpose,
      ...getNotes(state.facility, state.acceptLottery, newPurpose, state.commonFlag)
    }))
  }, [
    state?.commonFlag,
    state?.facility,
    state?.acceptLottery,
  ])

  // 病気変更
  const changeDropDownSelectedDiagonosis = useCallback(
    (field: 'first' | 'second') => (event: SelectChangeEvent<string>) => {
      if (!state) return;

    const diagonosisValue = event.target.value;
    if (!diagonosisValue) return;
  
      setState((prev) => ({
        ...prev as PageState,
        diagonosis: {
          ...(prev?.diagonosis ?? { first: null, second: null }),
          [field]: diagonosisValue,
        },
      }))
    },
    [state]
  )
  
  const handleChangeFirst = (event: SelectChangeEvent<string>) => {
    changeDropDownSelectedDiagonosis('first')(event);
  };
  
  const handleChangeSecond = (event: SelectChangeEvent<string>) => {
    changeDropDownSelectedDiagonosis('second')(event);
  };  

  const callTel = useCallback((tel: string) => (document.location.href = `tel:${tel}`), []);

  return {
    state,
    formMethods,
    currentLocation,
    isLoggedIn,
    changeChildBirthday,
    changeDate,
    callTel,
    sysCtrl,
    purposeOfUse,
    useReasonDetailIrregular,
    useReasonDetailEmg,
    useReasonDetailRefresh,
    onChangeUseReason,
    handleChangeFirst,
    handleChangeSecond
  };
}

/**

 * IDとして渡せるようにDateを文字列に変更

 * @param birthday 

 * @returns YYYYMMDD形式の文字列に変更

 */
const convertChildBirthdayToIdLike = (birthday: Date): string => {
  return `${birthday.getFullYear()}${(birthday.getMonth() + 1).toString().padStart(2, "0")}${birthday.getDate().toString().padStart(2, "0")}`
}

export function convertToDate(dateString: string): Date {
  // YYYYMMDD形式の文字列から年、月、日を抽出
  const year = parseInt(dateString.substring(0, 4), 10);
  const month = parseInt(dateString.substring(4, 6), 10) - 1; // JavaScriptのDateは月が0から始まるため -1
  const day = parseInt(dateString.substring(6, 8), 10);

  // 抽出した年、月、日を使用してDateオブジェクトを作成
  return new Date(year, month, day);
}

async function getUseReasonMaster() {
  return await getUseReason()
}

/**

 * 施設の受け入れ上限年齢から利用できるお子様の下限生年月日を算出

 * @param acceptAgeFrom

 * @return Date 基準日　

 */
const getAcceptBirthdayFrom = (acceptAgeTo: AcceptAge, datetime: Date) => {
  if( acceptAgeTo.classFlag &&  acceptAgeTo.age ) {
    return getFromToBirthdayFromClassAge(datetime, acceptAgeTo.age).from
  }else {
    return getBirthday(datetime, acceptAgeTo.age, acceptAgeTo.month, acceptAgeTo.day, false);
  }
}

/**

 * 施設の受け入れ下限年齢から利用できるお子様の上限生年月日を算出

 * 最大値は当日になる

 * @param acceptAgeTo

 * @return Date 基準日

 */
const getAcceptBirthdayTo = (acceptAgeFrom: AcceptAge, datetime: Date) => {
  let resultDate: Date;
  if (acceptAgeFrom.classFlag && acceptAgeFrom.age) {
    resultDate = getFromToBirthdayFromClassAge(datetime, acceptAgeFrom.age).to;
  } else {
    resultDate = getBirthday(datetime, acceptAgeFrom.age, acceptAgeFrom.month, acceptAgeFrom.day, true);
  }
  
  // 生後57日の生年月日
  const maxReservableBirthday = getBirthday(datetime, null, null, 57, true);

  // 生後57日の生年月日と比較して、日付を返す
  if (resultDate >= maxReservableBirthday) {
    return maxReservableBirthday;
  } else {
    return resultDate;
  }
}

/**

 * 施設に紐づいたお子さまデータを取得する

 * @param facilityId 施設ID

 * @return Child型に成形したお子さまデータ

 */
const getChildrenData = async(facilityId: string): Promise<Child[]> => {
  const childrenResponse = await executeGetChildren({ 
    facilityId,
    getLatestInterviewedFlag: yesNo.yes
  })

  return childrenResponse.result.map((child) => ({ value: child.childId, label: child.name }));
}

/**

 * 一週間分の空き状況を取得する

 * @param facilityId 

 * @param selectedChildIds 

 * @param targetDate 

 * @param useReasonCode

 * @returns 

 */
const getAvaiabilitiesForWeek = async (
  facilityId: string,
  childBirthday: Date,
  targetDate: Date,
  useReasonCode: string | null,
  diagonosis: {first: string | null, second: string | null}
) => {
  const response = await executeGetFacilityReservationsWeekPublic(facilityId, {
    childBirthday: convertChildBirthdayToIdLike(childBirthday),
    targetDate: toApiYmd(targetDate),
    useReasonCode: useReasonCode ?? purposeOfUseValue.common,
    diagonosis
  });
  return response.result;
}

interface InvalidChildBirthday {
  beforeMin: boolean,
  overMax: boolean
}
/**

 * お子さまの生年月日が下限・上限に収まっていないかどうかを確かめる。

 * 範囲内の場合はfalse、範囲外の場合は

 * @param childBirthday 

 * @param min 

 * @param max 

 * @returns 

 */
const isChildBirthdayInvalid = (childBirthday: Date, min: Date, max: Date): false | InvalidChildBirthday => {
  const returnObject: InvalidChildBirthday =  {
    beforeMin: childBirthday < min,
    overMax: childBirthday > max
  }
  return Object.values(returnObject).some((value) => value) ? returnObject : false;
}
